<script setup lang="ts">
import { computed } from "vue";
import { formatNumber } from "@/utils/number";
import { formatFromISOString } from "@/utils/date";
import { PointHistory } from "@/types/worker/PointHistory";

const props = defineProps<{
  pointHistory: PointHistory;
}>();

const isAddPoint = computed(() => {
  return props.pointHistory.deltaPoint > 0;
});

const isInit = computed(() => {
  return props.pointHistory.operationType === "init";
});

const pointHistoryTextColor = computed(() => {
  if (isInit.value) {
    return "text-green";
  }
  return isAddPoint.value ? "text-blue" : "text-red";
});

const formattedDeltaPoint = computed(() => {
  const pointLabel = `${formatNumber(props.pointHistory.deltaPoint)} pt`;
  // 初期追加ポイントの場合は符号なし
  if (isInit.value) {
    return pointLabel;
  }
  const sign = isAddPoint.value ? "+" : "";
  return `${sign}${pointLabel}`;
});

const formattedDate = computed(() => {
  // 初期追加ポイントの場合は日付表示なし
  if (isInit.value) {
    return "";
  }
  return formatFromISOString(props.pointHistory.createdAt, "yyyy/MM/dd");
});

const operationTypeToText = computed(() => {
  const typeToDisplayName: Record<string, string> = {
    init: "",
    refer_promotion: "招待によるポイント付与",
    referred_promotion: "招待されたことによるポイント付与",
    work_completion: "勤務完了よるポイント付与",
    admin_operation_manual: "キャンペーンなどによるポイント付与",
    recruitment_bonus_manual: "転職によるポイント付与",
    exchange: "ポイント交換",
    expiration: "有効期限切れによる失効",
    withdrawal: "退会などによるポイント失効",
    others: "その他"
  };
  return typeToDisplayName[props.pointHistory.operationType] || "";
});
</script>

<template>
  <div class="bg-white border-bottom border-outline-color">
    <div class="d-block p-3">
      <div class="d-flex justify-content-between align-items-center">
        <p class="text-size-14 text-weight-500" data-testid="createdAt">
          {{ formattedDate }}
        </p>
        <div class="d-flex align-items-center">
          <p
            class="text-size-14 text-weight-600"
            :class="pointHistoryTextColor"
            data-testid="deltaPoint"
          >
            {{ formattedDeltaPoint }}
          </p>
        </div>
      </div>
      <div class="mt-1">
        <p class="text-gray-05 text-size-12" data-testid="operationType">
          {{ operationTypeToText }}
        </p>
      </div>
    </div>
  </div>
</template>
